import { useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import dispatchChartService from "api/dispatchChartService";
import mailroomService from "api/mailroomService";
import {
  EParcelDeliveryCategory,
  IDispatchListItem,
} from "dto/Mailroom/IDispatchChart";
import { ENotificationType, notification } from "utils/notification";

import styles from "./outgoing.module.scss";

interface Props {
  /** Jedna przesyłka (menu wiersza) albo wielowybór z listy (masowa zmiana). */
  items: IDispatchListItem[];
  onClose: () => void;
  onDone: () => void;
}

// „Zmień sposób nadania" — zmiana formy doręczenia (ParcelDeliveryKind) przesyłek
// wychodzących na liście „Do wysyłki" (np. List ZPO → List polecony). Dla wielu przesyłek
// = port MassChangeOutgoingParcelType z Magistratu: jedna forma dla całego zaznaczenia,
// BE waliduje każdą osobno i zwraca, których nie udało się zmienić.
const ChangeDeliveryKindModal = ({ items, onClose, onDone }: Props) => {
  const { mutate, isLoading } =
    dispatchChartService.useChangeDeliveryKindMany();
  const { data: kindsData, isLoading: loadingKinds } =
    mailroomService.useGetDeliveryKinds();

  const many = items.length > 1;
  const first = items[0];

  // Model szyfrowania obecnej formy (elektroniczna vs fizyczna). Zmiana sposobu nadania
  // może zmienić kategorię i kanał (parcel.Type), ale TYLKO w obrębie tego samego modelu —
  // BE blokuje przejścia fizyczny↔elektroniczny (rozjazd szyfrowania załączników).
  const currentIsElectronic = useMemo(() => {
    const cur = (kindsData?.data ?? []).find(
      (k) => k.Id === first?.DeliveryKindId
    );
    return cur?.IsElectronic ?? false;
  }, [kindsData, first?.DeliveryKindId]);

  // EBI to osobny model (zaszyfrowane jak papier, adresat wskazany PESEL-em) — BE blokuje
  // zamianę EBI ↔ inne kanały, więc pokazujemy tylko formy z tej samej strony granicy.
  const currentIsEbi = useMemo(() => {
    const cur = (kindsData?.data ?? []).find(
      (k) => k.Id === first?.DeliveryKindId
    );
    return cur?.Category === EParcelDeliveryCategory.Ebi;
  }, [kindsData, first?.DeliveryKindId]);

  // Zaznaczenie mieszające papier i elektronikę — część odpadnie na BE; ostrzegamy.
  const mixedModels = useMemo(() => {
    const all = kindsData?.data ?? [];
    const models = new Set(
      items.map(
        (it) =>
          all.find((k) => k.Id === it.DeliveryKindId)?.IsElectronic ?? false
      )
    );
    return models.size > 1;
  }, [kindsData, items]);

  // Pokazujemy realne formy tego samego modelu szyfrowania, z pominięciem „Inne"
  // (forma-zaślepka, nie realny sposób nadania).
  const kinds = useMemo(() => {
    return (kindsData?.data ?? [])
      .filter(
        (k) =>
          k.Name !== "Inne" &&
          (k.IsElectronic ?? false) === currentIsElectronic &&
          (k.Category === EParcelDeliveryCategory.Ebi) === currentIsEbi
      )
      .slice()
      .sort((a, b) => a.SortOrder - b.SortOrder);
  }, [kindsData, currentIsElectronic, currentIsEbi]);

  const [kindId, setKindId] = useState<number | null>(
    first?.DeliveryKindId ?? null
  );

  // Zapis ma sens tylko, gdy choć jedna z przesyłek zmieni formę (przy wielowyborze kindId
  // jest wstępnie ustawione na formę pierwszej — bez tego przycisk wysyłałby pustą zmianę).
  const changed =
    kindId != null && items.some((it) => it.DeliveryKindId !== kindId);

  const submit = () => {
    if (kindId == null) return;
    mutate(
      { ParcelIds: items.map((x) => x.Id), DeliveryKindId: kindId },
      {
        onSuccess: (res) => {
          const { Changed, Failed } = res.data;
          if (Changed > 0) {
            notification({
              type: ENotificationType.SUCCESS,
              title: "Zmieniono sposób nadania",
              text: many
                ? `Zmieniono ${Changed} z ${items.length} przesyłek.`
                : `Przesyłka ${
                    first.ParcelNumber ?? ""
                  } — nowa forma doręczenia.`,
            });
          }
          if (Failed.length > 0) {
            notification({
              type:
                Changed > 0 ? ENotificationType.INFO : ENotificationType.ERROR,
              title: many
                ? `Nie zmieniono ${Failed.length} przesyłek`
                : "Nie udało się zmienić sposobu nadania",
              text: Failed.slice(0, 3)
                .map((f) =>
                  f.ParcelNumber ? `${f.ParcelNumber}: ${f.Message}` : f.Message
                )
                .join(" "),
            });
          }
          if (Changed > 0) onDone();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text:
              (e as { response?: { data?: { Message?: string } } })?.response
                ?.data?.Message ?? "Nie udało się zmienić sposobu nadania.",
          }),
      }
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>
          {many
            ? `Zmień sposób nadania — ${items.length} przesyłek`
            : "Zmień sposób nadania"}
        </div>
        <div className={styles.modalBody}>
          {many ? (
            <div className={styles.parcelPreview}>
              {items.map((it) => (
                <div key={it.Id} className={styles.previewRow}>
                  <span className={styles.sig}>{it.ParcelNumber ?? "—"}</span>
                  <span>{it.ReceiverName ?? "—"}</span>
                  <span className={styles.dim}>
                    {it.DeliveryKindName ?? ""}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className={styles.modalRow}>
                <span>Adresat:</span>
                <strong>{first.ReceiverName ?? "—"}</strong>
              </div>
              {first.ParcelNumber && (
                <div className={styles.modalRow}>
                  <span>Sygnatura:</span>
                  <strong>{first.ParcelNumber}</strong>
                </div>
              )}
              <div className={styles.modalRow}>
                <span>Obecna forma:</span>
                <strong>{first.DeliveryKindName ?? "—"}</strong>
              </div>
            </>
          )}

          <div className={styles.field}>
            <label>Nowa forma doręczenia</label>
            <select
              value={kindId ?? ""}
              onChange={(e) =>
                setKindId(e.target.value === "" ? null : Number(e.target.value))
              }
              disabled={loadingKinds}
            >
              {loadingKinds && <option value="">Ładowanie…</option>}
              {kinds.map((k) => (
                <option key={k.Id} value={k.Id}>
                  {k.Name}
                </option>
              ))}
            </select>
          </div>

          {mixedModels && (
            <div className={styles.warn}>
              Zaznaczenie łączy przesyłki papierowe i elektroniczne — zmiana
              obejmie tylko te o tym samym kanale co pierwsza.
            </div>
          )}

          <div className={styles.hint}>
            Zmiana możliwa dla przesyłek jeszcze nienadanych i niedołączonych do
            karty nadawczej. Koperta wspólna przenosi formę na zgrupowane pisma.
          </div>
        </div>
        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Zapisz"
            onClick={submit}
            loading={isLoading}
            disabled={!changed}
          />
        </div>
      </div>
    </div>
  );
};

export default ChangeDeliveryKindModal;
